import React from 'react';
import { Card } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Button } from '../../components/ui/button';
import { Plus, Trash2, Ship } from 'lucide-react';
import SearchableSelect from '../../components/SearchableSelect';
import { CONTAINER_TYPE_OPTIONS } from '../../constants/containerTypes';

const ContainerDetailsSection = ({ displayContainers, containerDetails, addContainer, removeContainer, updateContainer }) => (
  <Card className="bg-white rounded-xl border border-gray-100 shadow-sm">
    <div className="border-b-4 border-teal-500 px-6 py-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Ship className="w-5 h-5 text-teal-500" />
          <h2 className="text-lg font-semibold text-text-dark font-manrope">CONTAINER DETAILS</h2>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={addContainer}
          className="border-teal-500 text-teal-600 hover:bg-teal-50 text-xs"
          data-testid="add-container-btn"
        >
          <Plus className="w-3.5 h-3.5 mr-1" />Add Container
        </Button>
      </div>
    </div>

    <div className="p-6 space-y-4">
      {displayContainers.map((row, idx) => (
        <div key={idx} className="border border-gray-100 rounded-lg p-4 bg-gray-50/50 relative">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-semibold text-teal-600 font-ibm">Container #{idx + 1}</span>
            {containerDetails.length > 1 && (
              <button
                type="button"
                onClick={() => removeContainer(idx)}
                className="text-red-400 hover:text-red-600 p-1 rounded transition-colors"
                data-testid="remove-container-btn"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
            {/* Container No */}
            <div className="space-y-1">
              <Label className="text-text-dark font-ibm font-medium text-xs">Container No.</Label>
              <Input
                value={row.container_no || ''}
                onChange={e => updateContainer(idx, 'container_no', e.target.value)}
                placeholder="e.g. ABCU1234567"
                className="h-8 text-sm border-gray-200"
                data-testid="container-no-input"
              />
            </div>

            {/* Container Type — searchable dropdown */}
            <div className="space-y-1">
              <Label className="text-text-dark font-ibm font-medium text-xs">Container Type</Label>
              <SearchableSelect
                data-testid="container-type-select"
                value={row.container_type || ''}
                onValueChange={val => updateContainer(idx, 'container_type', val)}
                placeholder="Select type..."
                searchPlaceholder="Search container type..."
                options={CONTAINER_TYPE_OPTIONS}
              />
            </div>

            {/* C Seal No */}
            <div className="space-y-1">
              <Label className="text-text-dark font-ibm font-medium text-xs">C Seal No.</Label>
              <Input
                value={row.c_seal_no || ''}
                onChange={e => updateContainer(idx, 'c_seal_no', e.target.value)}
                placeholder=""
                className="h-8 text-sm border-gray-200"
                data-testid="container-c-seal-input"
              />
            </div>

            {/* L Seal No */}
            <div className="space-y-1">
              <Label className="text-text-dark font-ibm font-medium text-xs">L Seal No.</Label>
              <Input
                value={row.l_seal_no || ''}
                onChange={e => updateContainer(idx, 'l_seal_no', e.target.value)}
                placeholder=""
                className="h-8 text-sm border-gray-200"
                data-testid="container-l-seal-input"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {[
              { field: 'packages',     label: 'Packages' },
              { field: 'gross_weight', label: 'Gross Weight' },
              { field: 'net_weight',   label: 'Net Weight' },
              { field: 'cbm',          label: 'CBM' },
            ].map(({ field, label }) => (
              <div key={field} className="space-y-1">
                <Label className="text-text-dark font-ibm font-medium text-xs">{label}</Label>
                <Input
                  type="number"
                  min="0"
                  step="any"
                  value={row[field] || ''}
                  onChange={e => updateContainer(idx, field, e.target.value)}
                  placeholder="0"
                  className="h-8 text-sm border-gray-200"
                  data-testid={`container-${field}-input`}
                />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  </Card>
);

export default ContainerDetailsSection;
